import React, { useState } from 'react';
import MaskedInput from 'react-text-mask';
import { CategoriaCircularizacao, INovoRegistroManual } from '../interfaces/ICircularizacao';
import { MASCARA_CNPJ, MASCARA_CPF } from '../../../../utils/Constants';
import { JUSTIFICATIVA_PADRAO_INCLUSAO } from '../utils/justificativasPadrao';

type TipoDocumento = 'CNPJ' | 'CPF';

interface Props {
  categoria: CategoriaCircularizacao;
  categoriaLabel: string;
  salvando: boolean;
  onCancelar: () => void;
  // `continuar=true` (seção 12.4 — "inclusão em lote") mantém o modal aberto para o auditor
  // acrescentar o próximo destinatário em sequência, sem precisar voltar à tela inicial.
  onConfirmar: (dados: INovoRegistroManual, continuar: boolean) => void;
}

// Seção 4.1 do documento — "+ Incluir Empresa". Justificativa da Inclusão é obrigatória.
const IncluirEmpresaModal: React.FC<Props> = ({ categoria, categoriaLabel, salvando, onCancelar, onConfirmar }) => {
  const [razaoSocial, setRazaoSocial] = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [tipoDocumento, setTipoDocumento] = useState<TipoDocumento>('CNPJ');
  const [cnpj, setCnpj] = useState('');
  const [justificativa, setJustificativa] = useState(JUSTIFICATIVA_PADRAO_INCLUSAO);
  const [observacoes, setObservacoes] = useState('');
  const [tentouEnviar, setTentouEnviar] = useState(false);
  const [incluidasNestaSessao, setIncluidasNestaSessao] = useState(0);

  const mascaraAtual = tipoDocumento === 'CNPJ' ? MASCARA_CNPJ : MASCARA_CPF;

  const alterarTipoDocumento = (tipo: TipoDocumento) => {
    if (tipo === tipoDocumento) return;
    setTipoDocumento(tipo);
    setCnpj(''); // máscaras têm tamanhos diferentes — um valor parcial da outra não faz sentido.
  };

  const valido =
    razaoSocial.trim().length > 0 &&
    cnpj.trim().length === mascaraAtual.length &&
    justificativa.trim().length > 0;

  const confirmar = (continuar: boolean) => {
    setTentouEnviar(true);
    if (!valido) return;
    onConfirmar(
      {
        categoria,
        razaoSocial: razaoSocial.trim(),
        nomeFantasia: nomeFantasia.trim() || undefined,
        cnpj: cnpj.trim(),
        justificativaInclusao: justificativa.trim(),
        observacoesComplementares: observacoes.trim() || undefined,
      },
      continuar
    );
    if (continuar) {
      setIncluidasNestaSessao((v) => v + 1);
      setRazaoSocial('');
      setNomeFantasia('');
      setCnpj('');
      setJustificativa(JUSTIFICATIVA_PADRAO_INCLUSAO);
      setObservacoes('');
      setTentouEnviar(false);
    }
  };

  return (
    <div className='circ-modal-backdrop' onMouseDown={(e) => e.target === e.currentTarget && onCancelar()}>
      <div className='circ-modal'>
        <div className='circ-modal-header'>
          <div>
            <h4>+ Incluir Empresa — {categoriaLabel}</h4>
            {incluidasNestaSessao > 0 && (
              <div className='text-success' style={{ fontSize: 12 }}>
                <i className='bi bi-check-circle-fill me-1' />
                {incluidasNestaSessao} empresa{incluidasNestaSessao === 1 ? '' : 's'} incluída
                {incluidasNestaSessao === 1 ? '' : 's'} nesta sessão.
              </div>
            )}
          </div>
          <button type='button' className='btn btn-sm btn-icon btn-light' aria-label='Fechar' onClick={onCancelar}>
            <i className='bi bi-x-lg' />
          </button>
        </div>
        <div className='circ-modal-body'>
          <div className='circ-form-group'>
            <label className='circ-campo-obrigatorio'>Categoria da Circularização</label>
            <input className='circ-form-control' value={categoriaLabel} disabled />
          </div>
          <div className='circ-form-group'>
            <label className='circ-campo-obrigatorio'>Razão Social / Nome</label>
            <input
              className='circ-form-control'
              value={razaoSocial}
              onChange={(e) => setRazaoSocial(e.target.value)}
              placeholder='Ex.: Fornecedor Alfa Ltda.'
            />
          </div>
          <div className='circ-form-group'>
            <label>Nome Fantasia</label>
            <input className='circ-form-control' value={nomeFantasia} onChange={(e) => setNomeFantasia(e.target.value)} />
          </div>
          <div className='circ-form-group'>
            <label className='circ-campo-obrigatorio'>Tipo de Documento</label>
            <div className='circ-tabs'>
              <button
                type='button'
                className={`circ-tab ${tipoDocumento === 'CNPJ' ? 'circ-tab-ativa' : ''}`}
                onClick={() => alterarTipoDocumento('CNPJ')}
              >
                CNPJ
              </button>
              <button
                type='button'
                className={`circ-tab ${tipoDocumento === 'CPF' ? 'circ-tab-ativa' : ''}`}
                onClick={() => alterarTipoDocumento('CPF')}
              >
                CPF
              </button>
            </div>
          </div>
          <div className='circ-form-group'>
            <label className='circ-campo-obrigatorio'>{tipoDocumento}</label>
            <MaskedInput
              mask={mascaraAtual}
              className='circ-form-control'
              value={cnpj}
              onChange={(e) => setCnpj(e.target.value)}
              placeholder={tipoDocumento === 'CNPJ' ? '00.000.000/0000-00' : '000.000.000-00'}
            />
            {tentouEnviar && cnpj.trim().length !== mascaraAtual.length && (
              <div className='text-danger mt-1' style={{ fontSize: 12 }}>
                {cnpj.trim().length === 0 ? `${tipoDocumento} é obrigatório.` : `${tipoDocumento} incompleto.`}
              </div>
            )}
          </div>
          <div className='circ-form-group'>
            <label className='circ-campo-obrigatorio'>Justificativa da Inclusão</label>
            <textarea
              className='circ-form-control'
              rows={3}
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              placeholder='Ex.: Empresa incluída pelo auditor em razão da necessidade de confirmação de operações relevantes não contempladas na seleção preliminar.'
            />
            {tentouEnviar && justificativa.trim().length === 0 && (
              <div className='text-danger mt-1' style={{ fontSize: 12 }}>
                A justificativa da inclusão é obrigatória.
              </div>
            )}
          </div>
          <div className='circ-form-group'>
            <label>Observações Complementares</label>
            <textarea className='circ-form-control' rows={2} value={observacoes} onChange={(e) => setObservacoes(e.target.value)} />
          </div>
        </div>
        <div className='circ-modal-footer'>
          <button type='button' className='btn btn-sm btn-light' disabled={salvando} onClick={onCancelar}>
            {incluidasNestaSessao > 0 ? 'Concluir' : 'Cancelar'}
          </button>
          <button type='button' className='btn btn-sm btn-light-primary' disabled={salvando} onClick={() => confirmar(true)}>
            {salvando && <span className='spinner-border spinner-border-sm me-1' />}
            Incluir e Adicionar Outra
          </button>
          <button type='button' className='btn btn-sm btn-primary' disabled={salvando} onClick={() => confirmar(false)}>
            {salvando && <span className='spinner-border spinner-border-sm me-1' />}
            Incluir Empresa
          </button>
        </div>
      </div>
    </div>
  );
};

export default IncluirEmpresaModal;
